import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  canCancelAnyAccessRequest,
  canDecideOwnAccessRequest,
  canReviewAccessRequests,
} from '@infras/shared-types';
import { useMe } from '../lib/useMe';
import { RichEditor } from '../components/RichEditor';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { formatDate, formatDateTime } from '../lib/format';
import { Markdown } from '../components/Markdown';
import { FoldableMarkdown } from '../components/FoldableMarkdown';
import { MENTIONABLE_USERS } from '../lib/access';
import type { MentionUser } from '../lib/access';
import {
  ACCESS_REQUEST_FIELDS,
  ACCESS_REVIEWERS,
  statusBadgeClass,
} from '../lib/access';
import type { AccessRequest, AccessReviewer } from '../lib/access';

/** Avatar initials from a staff email: "dennis.nguyen@optisigns.com" → "DN". */
function initials(email: string): string {
  const [first, second] = email
    .split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean);
  return (
    ((first?.[0] ?? '') + (second?.[0] ?? '')).toUpperCase() ||
    email.slice(0, 1).toUpperCase()
  );
}

/** A person in the attribute list — initials avatar + the email itself. We show
 *  the full address, not a display name: infras has no name directory, and the
 *  email IS the identity every other surface (JWT, app_roles, MCP) keys on. */
function Person({ email }: { email: string }) {
  return (
    <span className="attrs__person">
      <span className="attrs__avatar" aria-hidden="true">
        {initials(email)}
      </span>
      <span className="access-detail-email" title={email}>
        {email}
      </span>
    </span>
  );
}

const NONE = <span className="muted">Not assigned</span>;

/**
 * Access-request detail — facts, answers, event timeline (the audit trail) and
 * the actions the caller's relationship to the request permits:
 *   requester → cancel (pending) · assignee|root|maintainer-role → decide /
 *   escalate / request sign-off · the STATED maintainer (email match) → record
 *   sign-off · anyone who can see it → comment.
 * The API re-checks everything — buttons here are UX, not enforcement.
 */
const REQUEST = gql`
  ${ACCESS_REQUEST_FIELDS}
  query AccessRequest($id: Int!) {
    accessRequest(id: $id) {
      ...AccessRequestFields
      assignees {
        reviewerEmail
        layer
        assignedBy
        assignedAt
      }
      answers {
        prompt
        answer
        sortOrder
      }
      events {
        id
        kind
        actorEmail
        note
        meta
        createdAt
        editedAt
      }
    }
  }
`;

const CANCEL = gql`
  mutation CancelAccessRequest($id: Int!) {
    cancelAccessRequest(id: $id) {
      id
      status
    }
  }
`;
const DECIDE = gql`
  mutation DecideAccessRequest($id: Int!, $approve: Boolean!, $note: String!) {
    decideAccessRequest(id: $id, approve: $approve, note: $note) {
      id
      status
    }
  }
`;
const ESCALATE = gql`
  mutation EscalateAccessRequest($id: Int!, $note: String) {
    escalateAccessRequest(id: $id, note: $note) {
      id
      currentLayer
    }
  }
`;
const REQUEST_SIGNOFF = gql`
  mutation RequestMaintainerSignoff($id: Int!) {
    requestMaintainerSignoff(id: $id) {
      id
      maintainerSignoff
    }
  }
`;
const RECORD_SIGNOFF = gql`
  mutation RecordMaintainerSignoff(
    $id: Int!
    $approve: Boolean!
    $note: String
  ) {
    recordMaintainerSignoff(id: $id, approve: $approve, note: $note) {
      id
      maintainerSignoff
    }
  }
`;
const COMMENT = gql`
  mutation AddAccessRequestComment($id: Int!, $note: String!) {
    addAccessRequestComment(id: $id, note: $note) {
      id
    }
  }
`;
const EDIT_COMMENT = gql`
  mutation EditAccessRequestComment($eventId: Int!, $note: String!) {
    editAccessRequestComment(eventId: $eventId, note: $note) {
      id
      editedAt
    }
  }
`;
const DELETE_COMMENT = gql`
  mutation DeleteAccessRequestComment($eventId: Int!) {
    deleteAccessRequestComment(eventId: $eventId)
  }
`;
const EDIT_NOTE = gql`
  mutation EditDecisionNote($id: Int!, $note: String!) {
    editDecisionNote(id: $id, note: $note) {
      id
      decisionNote
      decisionNoteEditedAt
      decisionNoteEditedBy
    }
  }
`;
const EDIT_DESC = gql`
  mutation EditAccessRequestDescription($id: Int!, $reason: String!) {
    editAccessRequestDescription(id: $id, reason: $reason) {
      id
      reason
      reasonEditedAt
      reasonEditedBy
    }
  }
`;

const EVENT_LABELS: Record<string, string> = {
  created: 'created the request',
  assigned: 'was assigned',
  escalated: 'escalated',
  maintainer_signoff_requested: 'requested maintainer sign-off',
  maintainer_signoff: 'recorded maintainer sign-off',
  commented: 'commented',
  decided: 'decided',
  cancelled: 'cancelled the request',
};

export function AccessRequestDetailPage() {
  const { id = '' } = useParams();
  const { me } = useMe();
  const { data, loading, error, refetch } = useQuery<{
    accessRequest: AccessRequest;
  }>(REQUEST, {
    variables: { id: Number(id) },
    fetchPolicy: 'cache-and-network',
  });
  const { data: revData } = useQuery<{ accessReviewers: AccessReviewer[] }>(
    ACCESS_REVIEWERS,
    {
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: mentionData } = useQuery<{ mentionableUsers: MentionUser[] }>(
    MENTIONABLE_USERS,
    {
      fetchPolicy: 'cache-and-network',
    },
  );
  const mentionUsers = mentionData?.mentionableUsers ?? [];

  const [mutErr, setMutErr] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [comment, setComment] = useState('');
  // Inline edit state: which comment event is being edited + its draft; the
  // decision-note editor toggle + its draft.
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');

  const onDone = () => {
    setMutErr(null);
    setNote('');
    void refetch();
  };
  const onErr = (e: Error) => setMutErr(e.message);
  const opts = { onCompleted: onDone, onError: onErr };

  const [cancel, { loading: l1 }] = useMutation(CANCEL, opts);
  const [decide, { loading: l2 }] = useMutation(DECIDE, opts);
  const [escalate, { loading: l3 }] = useMutation(ESCALATE, opts);
  const [requestSignoff, { loading: l4 }] = useMutation(REQUEST_SIGNOFF, opts);
  const [recordSignoff, { loading: l5 }] = useMutation(RECORD_SIGNOFF, opts);
  const [addComment, { loading: l6 }] = useMutation(COMMENT, {
    onCompleted: () => {
      setMutErr(null);
      setComment('');
      void refetch();
    },
    onError: onErr,
  });
  const [editComment, { loading: l7 }] = useMutation(EDIT_COMMENT, {
    onCompleted: () => {
      setMutErr(null);
      setEditingCommentId(null);
      setCommentDraft('');
      void refetch();
    },
    onError: onErr,
  });
  const [deleteComment, { loading: l9 }] = useMutation(DELETE_COMMENT, {
    onCompleted: () => {
      setMutErr(null);
      setDeleteTarget(null);
      void refetch();
    },
    onError: onErr,
  });
  const [editDecisionNote, { loading: l8 }] = useMutation(EDIT_NOTE, {
    onCompleted: () => {
      setMutErr(null);
      setEditingNote(false);
      setNoteDraft('');
      void refetch();
    },
    onError: onErr,
  });
  const [editDescription, { loading: l10 }] = useMutation(EDIT_DESC, {
    onCompleted: () => {
      setMutErr(null);
      setEditingDesc(false);
      setDescDraft('');
      void refetch();
    },
    onError: onErr,
  });
  const busy = l1 || l2 || l3 || l4 || l5 || l6 || l7 || l8 || l9 || l10;

  const r = data?.accessRequest;
  const myEmail = (me?.email ?? '').toLowerCase();
  const isRequester = r?.requesterEmail === myEmail;
  // Root may also cancel (housekeeping: requester offboarded / abandoned thread) —
  // mirrors the server guard on cancelAccessRequest.
  const canCancel = isRequester || (!!me && canCancelAnyAccessRequest(me.role));
  const isMaintainer = !!r?.maintainerEmail && r.maintainerEmail === myEmail;
  const isAssignee = !!r?.assignees?.some((a) => a.reviewerEmail === myEmail);
  const isReviewer = isAssignee || (!!me && canReviewAccessRequests(me.role));
  const pending = r?.status === 'pending';
  // root|maintainer may correct the decision note after a decision (matches the
  // server @RequireRole gate on editDecisionNote).
  const canEditNote =
    !!me && canReviewAccessRequests(me.role) && !!r?.decidedBy;
  // Next escalation target = the smallest reviewer layer above the current one
  // (layers may be sparse); undefined = already at the top.
  const nextLayer = useMemo(() => {
    const above = (revData?.accessReviewers ?? [])
      .map((x) => x.layer)
      .filter((l) => l > (r?.currentLayer ?? 0));
    return above.length ? Math.min(...above) : undefined;
  }, [revData, r?.currentLayer]);

  if (loading && !data) return <div className="page">Loading…</div>;
  if (error)
    return <div className="page muted">Failed to load: {error.message}</div>;
  if (!r)
    return (
      <div className="page">
        <p className="muted">Request not found.</p>
        <Link to="/access">← Back to access requests</Link>
      </div>
    );

  const vars = { id: Number(r.id) };
  // Root may decide its own request (self-documented grant); everyone else keeps four-eyes.
  const selfDecide = isRequester && !!me && canDecideOwnAccessRequest(me.role);
  const decideBlocked = isRequester && !selfDecide;
  const decideTitle = decideBlocked
    ? 'You cannot decide your own request'
    : !note.trim()
      ? 'Enter a decision note first'
      : selfDecide
        ? 'This is your own request — the decision will be marked self-decided'
        : '';

  return (
    <div className="page access-detail-page">
      <div className="detail-back">
        <Link to="/access" className="muted">
          ← Access requests
        </Link>
      </div>
      <div className="detail-head access-detail-head">
        <div className="access-detail-title">
          <p className="access-detail-reference">Request #{r.id}</p>
          <h1>{r.resourceName}</h1>
        </div>
        <span className={statusBadgeClass(r.status)}>{r.status}</span>
        {r.maintainerSignoff && (
          <span className="badge">maintainer: {r.maintainerSignoff}</span>
        )}
      </div>
      {/* plain text under the title (detail recipe: back → title → desc) — a
          bordered box here reads as an editable input, which it is not */}
      <div className="access-detail-layout">
        <div className="access-detail-main">
          <section
            className="access-detail-description"
            aria-labelledby="request-description-title"
          >
            <h2 id="request-description-title">Description</h2>
            {editingDesc ? (
              <>
                <ErrorBoundary
                  fallback={(m) => <p className="error">editor failed: {m}</p>}
                >
                  <RichEditor
                    value={descDraft}
                    onChange={setDescDraft}
                    placeholder="Describe what you need access to and why…"
                    disabled={busy}
                    mentionUsers={mentionUsers}
                  />
                </ErrorBoundary>
                <div className="btn-row access-detail-spaced">
                  <button
                    className="btn btn-primary"
                    disabled={busy || !descDraft.trim()}
                    onClick={() =>
                      void editDescription({
                        variables: {
                          id: Number(r.id),
                          reason: descDraft.trim(),
                        },
                      })
                    }
                  >
                    Save description
                  </button>
                  <button
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => {
                      setEditingDesc(false);
                      setDescDraft('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                {r.reason ? (
                  <Markdown>{r.reason}</Markdown>
                ) : (
                  <span className="desc-empty">No description provided.</span>
                )}
                <div className="access-detail-edit-meta">
                  {r.reasonEditedAt && (
                    <span className="muted text-2xs">
                      edited {formatDateTime(r.reasonEditedAt)}
                    </span>
                  )}
                  {/* Owner may correct/expand their own description; edit stamps reason_edited_at. */}
                  {isRequester && (
                    <button
                      className="mini-btn"
                      disabled={busy}
                      onClick={() => {
                        setEditingDesc(true);
                        setDescDraft(r.reason ?? '');
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </>
            )}
          </section>
          {mutErr && <p className="error">{mutErr}</p>}

          {r.decidedBy && (
            <>
              <h2 className="access-detail-section">Decision</h2>
              <div className="desc-card">
                <div className="tl-head access-detail-decision-meta">
                  <span className={statusBadgeClass(r.status)}>{r.status}</span>
                  <span className="tl-actor">by {r.decidedBy}</span>
                  {r.decisionNoteEditedAt && (
                    <span className="muted text-2xs">
                      · edited
                      {r.decisionNoteEditedBy
                        ? ` by ${r.decisionNoteEditedBy}`
                        : ''}
                    </span>
                  )}
                  {canEditNote && !editingNote && (
                    <button
                      className="mini-btn"
                      disabled={busy}
                      onClick={() => {
                        setEditingNote(true);
                        setNoteDraft(r.decisionNote ?? '');
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {r.decidedAt && (
                    <span className="tl-time">
                      {formatDateTime(r.decidedAt)}
                    </span>
                  )}
                </div>
                {editingNote ? (
                  <>
                    <ErrorBoundary
                      fallback={(m) => (
                        <p className="error">editor failed: {m}</p>
                      )}
                    >
                      <RichEditor
                        value={noteDraft}
                        onChange={setNoteDraft}
                        placeholder="decision note…"
                        disabled={busy}
                        mentionUsers={mentionUsers}
                      />
                    </ErrorBoundary>
                    <div className="btn-row access-detail-spaced">
                      <button
                        className="btn btn-primary"
                        disabled={busy || !noteDraft.trim()}
                        onClick={() =>
                          void editDecisionNote({
                            variables: {
                              id: Number(r.id),
                              note: noteDraft.trim(),
                            },
                          })
                        }
                      >
                        Save note
                      </button>
                      <button
                        className="btn btn-ghost"
                        disabled={busy}
                        onClick={() => {
                          setEditingNote(false);
                          setNoteDraft('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="tl-body">
                    <Markdown>{r.decisionNote ?? '—'}</Markdown>
                  </div>
                )}
              </div>
            </>
          )}

          {(r.answers?.length ?? 0) > 0 && (
            <>
              <h2 className="access-detail-section">Request answers</h2>
              {r.answers!.map((a) => (
                <div className="dep-card" key={a.sortOrder}>
                  <div className="access-detail-question">{a.prompt}</div>
                  <div style={{ marginTop: 4 }}>
                    <Markdown>{a.answer}</Markdown>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* ── actions ── */}
          {pending &&
            (isRequester ||
              isReviewer ||
              (isMaintainer && r.maintainerSignoff === 'requested')) && (
              <>
                <h2 className="access-detail-section">Review request</h2>
                {isReviewer && (
                  <>
                    <p className="muted access-detail-help">
                      A decision note is required to approve or deny — it's
                      recorded in the audit trail.
                    </p>
                    <ErrorBoundary
                      fallback={(m) => (
                        <p className="error">editor failed: {m}</p>
                      )}
                    >
                      <RichEditor
                        value={note}
                        onChange={setNote}
                        placeholder="decision / escalation note…"
                        disabled={busy}
                        mentionUsers={mentionUsers}
                      />
                    </ErrorBoundary>
                    {selfDecide && (
                      <p className="muted text-xs access-detail-spaced">
                        This is your own request. As root you may decide it —
                        the timeline will mark the decision{' '}
                        <strong>self-decided</strong> so it is clear no second
                        reviewer saw it.
                      </p>
                    )}
                    <div className="btn-row access-detail-spaced">
                      <button
                        className="btn btn-success"
                        disabled={busy || !note.trim() || decideBlocked}
                        title={decideTitle}
                        onClick={() =>
                          void decide({
                            variables: {
                              ...vars,
                              approve: true,
                              note: note.trim(),
                            },
                          })
                        }
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        disabled={busy || !note.trim() || decideBlocked}
                        title={decideTitle}
                        onClick={() =>
                          void decide({
                            variables: {
                              ...vars,
                              approve: false,
                              note: note.trim(),
                            },
                          })
                        }
                      >
                        Deny
                      </button>
                      <button
                        className="btn btn-warn"
                        disabled={busy || nextLayer == null}
                        title={
                          nextLayer == null
                            ? 'Already at the highest reviewer layer'
                            : ''
                        }
                        onClick={() =>
                          void escalate({
                            variables: { ...vars, note: note.trim() || null },
                          })
                        }
                      >
                        {nextLayer == null
                          ? 'Escalate'
                          : `Escalate to L${nextLayer}`}
                      </button>
                      {r.maintainerEmail && !r.maintainerSignoff && (
                        <button
                          className="btn"
                          disabled={busy}
                          onClick={() =>
                            void requestSignoff({ variables: vars })
                          }
                        >
                          Ask maintainer to sign off
                        </button>
                      )}
                    </div>
                  </>
                )}
                {isMaintainer && r.maintainerSignoff === 'requested' && (
                  <div className="btn-row">
                    <span className="muted">
                      You are the stated maintainer — sign off:
                    </span>
                    <button
                      className="btn btn-success"
                      disabled={busy}
                      onClick={() =>
                        void recordSignoff({
                          variables: { ...vars, approve: true, note: null },
                        })
                      }
                    >
                      Maintainer: approve
                    </button>
                    <button
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={() =>
                        void recordSignoff({
                          variables: { ...vars, approve: false, note: null },
                        })
                      }
                    >
                      Maintainer: decline
                    </button>
                  </div>
                )}
                {canCancel && (
                  <div className="btn-row">
                    <button
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={() => setConfirmCancel(true)}
                    >
                      {isRequester
                        ? 'Cancel my request'
                        : 'Cancel this request'}
                    </button>
                  </div>
                )}
              </>
            )}

          {/* ── timeline ── */}
          <h2 className="access-detail-section">Activity</h2>
          <div className="tl">
            {(r.events ?? []).map((e) => {
              const meta = safeParse(e.meta);
              const isComment = e.kind === 'commented';
              const canEditThis =
                isComment &&
                e.actorEmail === myEmail &&
                editingCommentId !== e.id;
              return (
                <div
                  key={e.id}
                  className={isComment ? 'tl-entry' : 'tl-entry tl-system'}
                >
                  <div className="tl-head">
                    <span className="tl-actor">{e.actorEmail}</span>
                    <span className="muted">
                      {EVENT_LABELS[e.kind] ?? e.kind}
                    </span>
                    {e.kind === 'decided' && meta.status ? (
                      <span>
                        — <strong>{String(meta.status)}</strong>
                      </span>
                    ) : null}
                    {e.kind === 'decided' && meta.selfDecided ? (
                      <span
                        className="badge warn"
                        title="Decided by the requester (root) — no second reviewer"
                      >
                        self-decided
                      </span>
                    ) : null}
                    {e.kind === 'escalated' && meta.toLayer ? (
                      <span>
                        to <strong>L{String(meta.toLayer)}</strong>
                      </span>
                    ) : null}
                    {e.kind === 'maintainer_signoff' && meta.signoff ? (
                      <span>
                        — <strong>{String(meta.signoff)}</strong>
                      </span>
                    ) : null}
                    {Array.isArray(meta.assignees) &&
                      meta.assignees.length > 0 && (
                        <span className="muted">
                          · assigned: {meta.assignees.join(', ')}
                        </span>
                      )}
                    {e.editedAt && <span className="muted">· edited</span>}
                    {canEditThis && (
                      <>
                        <button
                          className="mini-btn"
                          disabled={busy}
                          title="Edit this comment"
                          aria-label="Edit this comment"
                          onClick={() => {
                            setEditingCommentId(e.id);
                            setCommentDraft(e.note ?? '');
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="mini-btn mini-btn-danger"
                          disabled={busy}
                          title="Delete this comment"
                          aria-label="Delete this comment"
                          onClick={() => setDeleteTarget(e.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    <span className="tl-time">
                      {formatDateTime(e.createdAt)}
                    </span>
                  </div>
                  {editingCommentId === e.id ? (
                    <div className="tl-body">
                      <ErrorBoundary
                        fallback={(m) => (
                          <p className="error">editor failed: {m}</p>
                        )}
                      >
                        <RichEditor
                          value={commentDraft}
                          onChange={setCommentDraft}
                          placeholder="edit comment…"
                          disabled={busy}
                          mentionUsers={mentionUsers}
                        />
                      </ErrorBoundary>
                      <div className="btn-row access-detail-spaced">
                        <button
                          className="btn btn-sm btn-primary"
                          disabled={busy || !commentDraft.trim()}
                          onClick={() =>
                            void editComment({
                              variables: {
                                eventId: Number(e.id),
                                note: commentDraft.trim(),
                              },
                            })
                          }
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          disabled={busy}
                          onClick={() => {
                            setEditingCommentId(null);
                            setCommentDraft('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // the 'created' note IS the description (shown in the card above) — don't repeat it
                    e.note &&
                    e.kind !== 'created' && (
                      <div className="tl-body">
                        <FoldableMarkdown>{e.note}</FoldableMarkdown>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>

          <section
            className="access-detail-composer"
            aria-labelledby="add-comment-title"
          >
            <h3 id="add-comment-title">Add a comment</h3>
            <ErrorBoundary
              fallback={(m) => <p className="error">editor failed: {m}</p>}
            >
              <RichEditor
                value={comment}
                onChange={setComment}
                placeholder="add a comment…"
                disabled={busy}
                mentionUsers={mentionUsers}
              />
            </ErrorBoundary>
            <div className="btn-row access-detail-spaced">
              <button
                className="btn btn-primary"
                disabled={busy || !comment.trim()}
                onClick={() =>
                  void addComment({
                    variables: { ...vars, note: comment.trim() },
                  })
                }
              >
                Post comment
              </button>
            </div>
          </section>
        </div>
        <aside
          className="access-detail-facts"
          aria-labelledby="request-details-title"
        >
          <h2 id="request-details-title">Request details</h2>
          <dl className="attrs">
            {(
              [
                ['Requester', <Person email={r.requesterEmail} />],
                ['Resource type', r.resourceType],
                [
                  'Maintainer',
                  r.maintainerEmail ? (
                    <Person email={r.maintainerEmail} />
                  ) : (
                    NONE
                  ),
                ],
                ['Current layer', `L${r.currentLayer}`],
                [
                  'Duration',
                  r.durationKind === 'temporary' ? (
                    <>
                      Temporary
                      {r.expiresAt && <> — expires {formatDate(r.expiresAt)}</>}
                    </>
                  ) : (
                    'Permanent'
                  ),
                ],
                [
                  'Review path',
                  r.riskTier ? (
                    <span className={`risk-badge risk-badge--${r.riskTier}`}>
                      {r.riskTier}
                    </span>
                  ) : (
                    // Rows filed before migration 036 have no snapshot, and deriving
                    // one now would show a tier nobody reviewed under.
                    <span className="muted">not recorded</span>
                  ),
                ],
                [
                  'Assigned reviewers',
                  r.assignees?.length ? (
                    <div className="attrs__people">
                      {r.assignees.map((a) => (
                        <Person key={a.reviewerEmail} email={a.reviewerEmail} />
                      ))}
                    </div>
                  ) : (
                    NONE
                  ),
                ],
                ['Created', formatDateTime(r.createdAt)],
              ] as [string, React.ReactNode][]
            ).map(([k, v]) => (
              <div className="attrs__row" key={k}>
                <dt className="attrs__k">{k}</dt>
                <dd className="attrs__v">{v}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>
      <ConfirmDialog
        open={confirmCancel}
        title={
          isRequester
            ? 'Cancel this request?'
            : `Cancel ${r.requesterEmail}'s request?`
        }
        body="It stays in the system with status cancelled — the timeline is kept. The requester is emailed."
        confirmLabel="Yes, withdraw it"
        cancelLabel="Keep it open"
        danger
        busy={busy}
        onConfirm={() => {
          setConfirmCancel(false);
          void cancel({ variables: vars });
        }}
        onCancel={() => setConfirmCancel(false)}
      />
      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete this comment?"
        body="It is removed from the timeline for everyone. This cannot be undone."
        confirmLabel="Delete comment"
        danger
        busy={busy}
        onConfirm={() => {
          if (deleteTarget != null)
            void deleteComment({
              variables: { eventId: Number(deleteTarget) },
            });
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function safeParse(s: string): Record<string, unknown> {
  try {
    return JSON.parse(s) ?? {};
  } catch {
    return {};
  }
}
